import { LitElement, html, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { badgeStyles } from './badge.styles';

export type BadgeColor =
  'informative' | 'warning' | 'negative' | 'positive' | 'important' | 'pink' | 'neutral' | 'white' | 'black' | 'violet' | 'red' | 'recommendation';

export type BadgeVariant = 'solid' | 'subtle';
export type BadgeType = 'text' | 'numeric' | 'new' | 'beta';

@customElement('cds-badge')
export class BadgeElement extends LitElement {
  static styles: CSSResultGroup = [badgeStyles];

  // Public properties
  @property({ type: String }) color?: BadgeColor;
  @property({ type: String }) variant?: BadgeVariant;
  @property({ type: String }) type: BadgeType = 'text';
  @property({ type: Number }) max: number | null = 9999;
  @property({ type: Boolean }) overlap = false;
  @property({ type: Boolean }) above = false;

  // Internal state
  @state() private _hasIcon = false;
  @state() private _hasTemplate = false;
  
  private _mutationObserver?: MutationObserver;

  // Lifecycle methods
  connectedCallback() {
    super.connectedCallback();
    this._setupSlotObservers();
    this._mutationObserver = new MutationObserver(() => {
      this.requestUpdate();
    });
    this._mutationObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupObservers();
  }

  firstUpdated() {
    this._checkSlotContent();
  }

  // Private methods
  private _setupSlotObservers() {
    this.addEventListener('slotchange', this._handleSlotChange.bind(this));
  }

  private _cleanupObservers() {
    this.removeEventListener('slotchange', this._handleSlotChange.bind(this));
    this._mutationObserver?.disconnect();
  }

  private _handleSlotChange() {
    this._checkSlotContent();
  }

  private _checkSlotContent() {
    // Check for icon slot
    const iconElements = Array.from(this.children).filter(
      child => child.getAttribute('slot') === 'icon'
    );
    this._hasIcon = iconElements.length > 0;

    // Check for template slot
    const templateElements = Array.from(this.children).filter(
      child => child.getAttribute('slot') === 'template'
    );
    this._hasTemplate = templateElements.length > 0;

    // Update host class for icon positioning
    this.classList.toggle('with-icon', this._hasIcon);
  }

  // Default color logic for numeric
  private _getEffectiveColor(): BadgeColor | null {
    if (this.type === 'numeric' && (!this.color || this.color === 'red')) {
      return null;
    }
    return this.color || 'informative';
  }

  private _getEffectiveVariant(): BadgeVariant {
    // Numeric badges are always solid
    if (this.type === 'numeric') {
      return 'solid';
    }
    return this.variant || 'subtle';
  }

  private _processContent(content: string): string | number {
    switch (this.type) {
      case 'new':
        return 'NEW';
      case 'beta':
        return 'BETA';
      case 'numeric':
        return this._processNumericContent(content);
      default:
        return content;
    }
  }

  private _processNumericContent(content: string): string | number {
    const num = Number(content);

    // Handle invalid numbers
    if (isNaN(num) || num < 0) return content;

    // Handle max truncation
    if (this.max !== null && num > this.max) return `${this.max}+`;

    // Handle large number formatting (only when max is null)
    if (this.max === null) {
      if (num >= 1000000) {
        return this._formatLargeNumber(num, 1000000, 'M');
      }
      if (num >= 1000) {
        return this._formatLargeNumber(num, 1000, 'K');
      }
    }
    return num;
  }

  private _formatLargeNumber(num: number, divisor: number, suffix: string): string {
    const result = num / divisor;
    // Use fixed decimal places only when needed
    const formatted = result % 1 === 0 ? result.toString() : result.toFixed(1);
    return `${formatted}${suffix}`;
  }

  private _buildCssClasses(): string {
    const classes: string[] = ['badge-content'];
    
    const color = this._getEffectiveColor();
    if (color) {
      classes.push(color);
    }
    
    const variant = this._getEffectiveVariant();
    classes.push(variant);

    // Add type-specific classes
    if (this.type !== 'text') {
      classes.push(this.type);
    }

    return classes.join(' ');
  }

  private _renderContent() {
    if (this._hasTemplate) {
      return html`<slot name="template"></slot>`;
    }
    
    const textContent = this.textContent?.trim() || '';
    return this._processContent(textContent);
  }

  render() {
    return html`
      <span class="badge-wrapper">
        <slot name="icon"></slot>
        <span class="${this._buildCssClasses()}">
          ${this._renderContent()}
        </span>
      </span>
    `;
  }
}
